import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import i18n from '../i18n';

export type Language = 'en' | 'no';

const ALL_LANGUAGES: Language[] = ['en', 'no'];

interface LanguageContextType {
  language: Language;
  allowedLanguages: Language[];
  setLanguage: (lang: Language) => void;
  setAllowedLanguages: (langs: string[]) => void;
  t: (key: string, options?: any) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem('app_language');
    if (stored === 'en' || stored === 'no') return stored;
    // fall back to i18n detected language if available
    const detected = i18n?.language;
    if (detected?.startsWith('no') || detected?.startsWith('nb') || detected?.startsWith('nn')) return 'no';
    return 'en';
  });

  const [allowedLanguages, setAllowedLanguagesState] = useState<Language[]>(ALL_LANGUAGES);

  const setAllowedLanguages = (langs: string[]) => {
    const filtered = ALL_LANGUAGES.filter((l) => langs.includes(l));
    const resolved: Language[] = filtered.length > 0 ? filtered : ALL_LANGUAGES;
    setAllowedLanguagesState(resolved);
    // If current language is no longer allowed, switch to first allowed
    if (!resolved.includes(language)) {
      setLanguage(resolved[0]);
    }
  };

  useEffect(() => {
    localStorage.setItem('app_language', language);
  }, [language]);

  // Ensure i18n is synchronized with the initial language on mount.
  // This covers the case where `app_language` is present in localStorage
  // but i18n was initialized with the default (e.g., 'en') before
  // the provider mounted.
  useEffect(() => {
    if (i18n && typeof i18n.changeLanguage === 'function') {
      i18n.changeLanguage(language).catch(() => {});
    }
    // run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    if (i18n && typeof i18n.changeLanguage === 'function') {
      i18n.changeLanguage(lang).catch(() => {});
    }
  };

  const t = (key: string, options?: any): string => {
    if (i18n && typeof (i18n as any).t === 'function') {
      return (i18n as any).t(key, options);
    }
    // Fallback: return the key itself if i18n is not ready
    return key;
  };

  return (
    <LanguageContext.Provider value={{ language, allowedLanguages, setLanguage, setAllowedLanguages, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
